import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { fetchAccountContract, legacyRoleString } from "./contract";

/**
 * The member's capabilities and badges, held for the session (ECH-288).
 *
 * Replaces `localStorage.role` as the source of truth for what a member may do.
 * That string is client-owned and editable, built with `role.join("_")` and read
 * with `String.includes`, so a dual-role member is the literal `"mentee_mentor"`.
 *
 * `loaded` is tracked separately from the contract, because "not loaded yet" and
 * "loaded, permits nothing" are otherwise indistinguishable — and a guard that
 * cannot tell them apart will flash a member out of a page they are allowed to
 * see while the request is still in flight.
 */
const initialState = {
  contract: { capabilities: [], badges: [] },
  loaded: false,
  loading: false,
  activeRequestId: null,
};

/**
 * Keep `localStorage.role` in step with what the server says.
 *
 * A compatibility step, not a permanent one. 41 components still read that
 * string and turn it into a profile data key via `roleProfile()`, so it has to
 * be right — but it had three writers and no owner, and onboarding completion
 * hardcoded `"mentee"` regardless of the goals chosen. This makes the contract
 * load the single writer. See `legacyRoleString` for why the reads themselves
 * are not being rewritten as capability checks.
 *
 * Only ever writes a role it could derive: an empty result means the contract
 * permits nothing, and overwriting a good value with `""` would break every
 * profile editor for a member whose contract briefly came back empty.
 */
const syncLegacyRoleString = (contract) => {
  const role = legacyRoleString(contract);
  if (!role) return;
  try {
    localStorage.setItem("role", role);
  } catch {
    // Private mode or quota. The stored value stays stale rather than the load
    // failing, which is the lesser of the two.
  }
};

export const loadAccountContract = createAsyncThunk(
  "capabilities/load",
  async (_, { getState, requestId }) => {
    const contract = await fetchAccountContract();
    if (getState().capabilities.activeRequestId === requestId) {
      syncLegacyRoleString(contract);
    }
    return contract;
  },
  {
    condition: (_, { getState }) => {
      const state = getState().capabilities;
      return !state.loaded && !state.loading;
    },
  },
);

const capabilitySlice = createSlice({
  name: "capabilities",
  initialState,
  reducers: {
    /** On sign out, so the next member in this tab never inherits the last one's. */
    clearAccountContract: () => ({ ...initialState }),
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadAccountContract.pending, (state, action) => {
        state.loading = true;
        state.activeRequestId = action.meta.requestId;
      })
      .addCase(loadAccountContract.fulfilled, (state, action) => {
        if (state.activeRequestId !== action.meta.requestId) return;
        state.contract = action.payload;
        state.loaded = true;
        state.loading = false;
        state.activeRequestId = null;
      });
    // `fetchAccountContract` never rejects, but a thunk can still fail. Mark it
    // loaded either way: a failed load must resolve to "permits nothing" rather
    // than leaving every guard waiting, which reads as a permanently blank page.
    builder.addCase(loadAccountContract.rejected, (state, action) => {
      if (state.activeRequestId !== action.meta.requestId) return;
      state.contract = { capabilities: [], badges: [] };
      state.loaded = true;
      state.loading = false;
      state.activeRequestId = null;
    });
  },
});

export const { clearAccountContract } = capabilitySlice.actions;

export const selectContract = (state) => state.capabilities.contract;
export const selectContractLoaded = (state) => state.capabilities.loaded;
export const selectContractLoading = (state) => state.capabilities.loading;

export default capabilitySlice.reducer;
